import { useEffect, useMemo } from 'react';
import { adminCreateCampaign, adminGetCampaign, adminUpdateCampaign, campaignDefaultTemplate, campaignSelector, getCampaignDefaultTemplate, getCampaignTemplates, HelpTypes, setCampaignEmailTemplates, setPaymentLoad, utilResetValue } from '@pharmaplan/common';
import { DateTime } from 'luxon';
import { FormikValues, useFormik } from 'formik';
import { useLocation } from 'react-router-dom';
import { BroadcastGroupActionType, MomentTimeFormats } from '../../../helpers/Constants';
import { useAppSelector } from '../../useAppSelector';
import { useAppDispatch } from '../../useAppDispatch';
import useTransformCampaignTemplates from './useTransformCampaignTemplates';
import validator from '../../../components/Admin/Campaigns/CreateEditCampaign/validator';

const useCampaignStepper = (type: BroadcastGroupActionType) => {
  const dispatch = useAppDispatch();
  const campaign = useAppSelector(campaignSelector);

  const templateObj = useAppSelector(campaignDefaultTemplate);
  const { templates } = templateObj ?? {};

  const isEdit = type === BroadcastGroupActionType.edit;
  const savedTemplate = useAppSelector(getCampaignTemplates);
  const { imageTemplateConfig, stateToTemplateArray, templateArrayToState } = useTransformCampaignTemplates();

  const { state } = useLocation();
  const { campaignId } = (state ?? {}) as { campaignId?: string };

  const initialValues = useMemo(() => {
    if (isEdit) {
      return {
        name: campaign?.name || '',
        hourlyRateBonus: campaign?.hourlyRateBonus || '',
        userType: campaign?.userType?.toString() || HelpTypes.pharmacy,
        remotePromoHourlyRate: campaign?.remotePromoHourlyRate,
        promoHourlyRate: campaign?.promoHourlyRate || '',
        creditShiftCount: campaign?.creditShiftCount || '',
        validityInDays: campaign?.validityUntil
          ? DateTime.fromISO(campaign?.validityUntil, { zone: 'utc' })
          : '',
        startDate: campaign?.startDate
          ? DateTime.fromISO(campaign.startDate)
          : null,
        endDate: campaign?.endDate ? DateTime.fromISO(campaign.endDate) : null,
      };
    }
    return {
      name: '',
      userType: HelpTypes.pharmacy,
      promoHourlyRate: '',
      creditShiftCount: '',
      remotePromoHourlyRate: '',
      hourlyRateBonus: '',
      validityInDays: '',
      startDate: null,
      endDate: null,
    };
  }, [campaign, isEdit]);

  const resetPaymentLoad = () => {
    dispatch(setPaymentLoad(false));
  };

  const handleSubmit = async (values: FormikValues) => {
    dispatch(setPaymentLoad(true));
    const payload = {
      templates: stateToTemplateArray(savedTemplate),
      ...imageTemplateConfig(savedTemplate),
      name: values.name,
      userType: values.userType,
      promoHourlyRate: Number(values.promoHourlyRate),
      hourlyRateBonus: Number(values?.hourlyRateBonus),
      creditShiftCount: Number(values.creditShiftCount),
      remotePromoHourlyRate: Number(values?.remotePromoHourlyRate),
      validityUntil: values.validityInDays?.toFormat(
        MomentTimeFormats.fullDate,
      ),
      startDate: values.startDate
        ? values.startDate.toFormat(MomentTimeFormats.fullDate)
        : null,
      endDate: values.endDate
        ? values.endDate.toFormat(MomentTimeFormats.fullDate)
        : null,
    };

    if (isEdit && campaignId) {
      dispatch(adminUpdateCampaign({ ...payload, campaignId })).finally(
        resetPaymentLoad,
      );
      return;
    }

    dispatch(adminCreateCampaign(payload)).finally(resetPaymentLoad);
  };

  const formik = useFormik({
    initialValues,
    onSubmit: handleSubmit,
    enableReinitialize: true,
    validationSchema: validator(),
  });

  useEffect(() => {
    if (isEdit && campaignId) {
      dispatch(adminGetCampaign(campaignId));
    }
  }, [dispatch, isEdit, campaignId]);

  useEffect(() => {
    if (
      !isEdit
      || campaign?.campaignId !== campaignId
      || !campaign?.templates
    ) {
      return;
    }

    dispatch(
      setCampaignEmailTemplates({
        ...templateArrayToState(campaign.templates, savedTemplate),
        activationEmailImage: campaign.activationEmailUrl ?? '',
        registrationEmailImage: campaign.registrationEmailUrl ?? '',
        registrationPopupImage: campaign.registrationPopupUrl ?? '',
        bookingEmailImage: campaign.bookingEmailUrl ?? '',
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, campaignId, campaign]);

  useEffect(() => {
    if (!isEdit) {
      dispatch(
        setCampaignEmailTemplates(
          templateArrayToState(templates ?? [], savedTemplate),
        ),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, templates]);

  useEffect(() => {
    dispatch(getCampaignDefaultTemplate(formik.values.userType as HelpTypes));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.userType]);

  useEffect(() =>
    () => {
      dispatch(utilResetValue(['campaignEmailTemplates']));
    }, []);

  return { formik };
};

export default useCampaignStepper;
